/**
 * 모든 경로의 단일 출처 — 호출처는 이 객체로만 경로를 쓴다 (하드코딩 금지).
 * 도메인별로 묶어 합친다. 도메인 루트(`/auth`)는 상수로 두고 자식은 거기서 파생한다
 * (`${AUTH}/login`). 키는 도메인 접두어(`AUTH_LOGIN`). 동적 구간(`:param`)은 `buildPath`.
 */

const AUTH = '/auth';
const SUBSCRIPTIONS = '/subscriptions';
const AGREEMENTS = '/agreements';
const ERROR = '/error';
const STATS = '/stats';

const APP_ROUTES = {
  DASHBOARD: '/dashboard', // 대시보드(기본 착지점). 루트 '/' 는 라우트 트리 index 가 여기로 redirect.
  STATS_GENERATION: `${STATS}/generation`, // 발전통계 > 발전량 분석
  // ── 발전소 스코프 라우트 패턴 ────────────────────────────────────────────────────────────────────
  // 대시보드·발전량 분석·수익분석은 발전소 스코프를 **path 맨 뒤**로 운반한다(인버터 상세분석 정합). 세그먼트가
  // **선택적**(`:plantId?`)인 건 세 화면 모두 발전소 미지정 = **'전체' 스코프**라는 정상 상태가 있기 때문.
  // 조립은 `dashboardPath`·`generationPath`·`revenuePath`(선택적 segment 라 `buildPath` 대상 아님).
  //
  // **스코프별로 두 라우트로 쪼갠다**(대시보드·발전량 분석·수익분석 모두) — 단일은 전체에서 발전소를 골라
  // **들어간** 화면이라 뒤로가기가 필요한데, `isSubpage` 는 라우트별 선언이라 선택 세그먼트 **한** 라우트로는
  // 스코프별로 못 가른다(파생 금지 정책 — `routes/app.ts` 헤더). 그래서 단일 스코프만 `:plantId` **필수**
  // 세그먼트의 별도 라우트로 두고 `isSubpage: true` 를 단다(컴포넌트는 전체와 동일 — 화면이 아니라 위계 선언이
  // 갈리는 것뿐이다).
  DASHBOARD_SINGLE: '/dashboard/:plantId',
  STATS_GENERATION_SCOPED: `${STATS}/generation/:plantId`,
  REVENUE_SCOPED: '/revenue/:plantId',
  STATS_INVERTER: `${STATS}/inverter`, // 발전통계 > 인버터 상세분석
  // 인버터 상세분석 드릴인(탭 + 발전소). 조립은 `inverterAnalysisPath`(탭 선택적) — 이 상수는 **라우트 정의·위계
  // 판정용 패턴**이라 `buildPath` 대상이 아니다(선택 segment `:tab?`).
  STATS_INVERTER_DETAIL: `${STATS}/inverter/:tab?/:plantId`,
  REVENUE: '/revenue', // 수익분석
  ALERTS: '/alerts', // 이상알림
  MY: '/my', // 정보관리 > 마이페이지
  PLANTS: '/plants', // 정보관리 > 발전소정보 (목록)
  PLANT_INFO_DETAIL: '/plants/:plantId', // 발전소 정보 상세 (기본/설치/계약) — 식별자 맨 뒤
  PLANT_USE_SETUP: '/plants/:plantId/use', // 발전소 용도/계약 설정·변경 (정보 상세에서 진입)
  SUPPORT: '/support', // 고객센터
  SETTINGS: '/settings', // 설정 (알림·수신동의·테마)
} as const;

const AUTH_ROUTES = {
  AUTH,
  AUTH_LOGIN: `${AUTH}/login`,
  AUTH_CONSENT: `${AUTH}/consent`,
  AUTH_PASSWORD: `${AUTH}/password`,
} as const;

const PERMISSION_ROUTES = {
  PERMISSION: '/permission',
} as const;

/**
 * 강제 업데이트 — `/auth` 와 **같은 최상위 레벨**(앱 본체 밖). 하위호환이 끊긴 구버전은 어떤 화면도
 * 신뢰할 수 없어 본체 라우트 아래 두지 않는다. native 전용 도달점(web 은 새로고침 다이얼로그로 해소).
 */
const UPDATE_ROUTES = {
  UPDATE_REQUIRED: '/update-required',
} as const;

const SUBSCRIPTION_ROUTES = {
  SUBSCRIPTIONS, // 발전소 목록 = `/subscriptions`(인덱스). (구 `/subscriptions/list` 폐지)
  // 구독유형 선택 — 발전소를 **path param**(`:plantId`)으로. nav 는 `buildPath(SUBSCRIPTIONS_PLAN, { plantId })`.
  // (구 `/subscriptions/plan?plantId=` 폐지). static `result`·`callback` 보다 매칭 우선순위 낮음(RR static>dynamic).
  SUBSCRIPTIONS_PLAN: `${SUBSCRIPTIONS}/:plantId`,
  // Toss 카드 등록 인증창 콜백 목적지. `:result` = success | fail 단일 동적 라우트
  // (`buildPath` 로 분기). 컨텍스트(연동종류·복귀경로)는 `?state=`(인코딩)로 전달.
  // 성공 시 authKey·customerKey 쿼리 수신. 핸들러(빌링키 발급 POST /billings)는 후속.
  SUBSCRIPTIONS_CALLBACK: `${SUBSCRIPTIONS}/callback/:result`,
  // 결제 결과 화면(완료/실패). 메시지·구분은 `?state=`(`ResultState` 인코딩)로 전달.
  SUBSCRIPTIONS_RESULT: `${SUBSCRIPTIONS}/result`,
} as const;

/** 약관 코드: terms | privacy | refund (BE agreementType 정합). */
const AGREEMENT_ROUTES = {
  AGREEMENTS,
  AGREEMENT_DETAIL: `${AGREEMENTS}/:code`,
} as const;

const ERROR_ROUTES = {
  ERROR,
  ERROR_MAINTENANCE: `${ERROR}/maintenance`,
  ERROR_UNAUTHORIZED: `${ERROR}/unauthorized`,
} as const;

export const ROUTES = {
  ...APP_ROUTES,
  ...AUTH_ROUTES,
  ...PERMISSION_ROUTES,
  ...UPDATE_ROUTES,
  ...SUBSCRIPTION_ROUTES,
  ...AGREEMENT_ROUTES,
  ...ERROR_ROUTES,
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** 패턴의 `:param` 들을 타입 키로 추출 (`/a/:x/b/:y` → `'x' | 'y'`). */
type ExtractParams<T extends string> = T extends `${string}:${infer P}/${infer Rest}`
  ? P | ExtractParams<`/${Rest}`>
  : T extends `${string}:${infer P}`
    ? P
    : never;

/**
 * 동적 경로 바인딩. 누락 키는 throw(빈 구간 사고 방지), 값은 `encodeURIComponent`.
 * 예: `buildPath(ROUTES.AGREEMENT_DETAIL, { code: 'terms' })` → `/agreements/terms`.
 */
export function buildPath<T extends string>(
  pattern: T,
  params: Record<ExtractParams<T>, string>,
): string {
  return pattern.replace(/:([a-zA-Z][a-zA-Z0-9_]*)/g, (_, key: string) => {
    const value = (params as Record<string, string>)[key];
    if (value === undefined) {
      throw new Error(`buildPath: missing param "${key}" for pattern "${pattern}"`);
    }
    return encodeURIComponent(value);
  });
}

/**
 * 인버터 상세분석 드릴인 path — **탭 + 발전소(정체성)를 path segment, 식별자(plantId)를 맨 뒤**로
 * (`/stats/inverter/:tab?/:plantId`, admin plant-detail 정합). 탭 없으면 `/stats/inverter/:plantId`(단일 세그=plantId,
 * RR 실측 확인). 인버터 분석은 "전체 발전소" 없어 무플랜트 진입은 firstPlant materialize. rtuId·필터는 URL 미운반.
 */
export const inverterAnalysisPath = (plantId: string, tab?: string): string =>
  `${ROUTES.STATS_INVERTER}/${tab ? `${tab}/` : ''}${encodeURIComponent(plantId)}`;

/**
 * 발전소 스코프 path 조립(대시보드·발전량 분석) — 발전소 지정이면 `<base>/<plantId>`, **미지정(전체)이면 base**.
 * `null`·`undefined` 를 모두 전체로 받아 호출처가 "전체 = 값 없음" 을 그대로 넘길 수 있다.
 */
const scopedPath = (base: string, plantId?: string | null): string =>
  plantId ? `${base}/${encodeURIComponent(plantId)}` : base;

/** 대시보드 path — 전체 `/dashboard` · 단일 `/dashboard/<plantId>`. */
export const dashboardPath = (plantId?: string | null): string =>
  scopedPath(ROUTES.DASHBOARD, plantId);

/** 발전량 분석 path — 전체 `/stats/generation` · 단일 `/stats/generation/<plantId>`. */
export const generationPath = (plantId?: string | null): string =>
  scopedPath(ROUTES.STATS_GENERATION, plantId);

/** 수익분석 path — 전체 `/revenue` · 단일 `/revenue/<plantId>`. */
export const revenuePath = (plantId?: string | null): string => scopedPath(ROUTES.REVENUE, plantId);

/** 발전소 정보 상세 path — `/plants/:plantId`(식별자 맨 뒤). 목록·수익·대시보드 팝업의 단일 진입. */
export const plantInfoPath = (plantId: string): string =>
  `${ROUTES.PLANTS}/${encodeURIComponent(plantId)}`;

/** 발전소 용도/계약 설정·변경 path — `/plants/:plantId/use`. 정보 상세 액션에서 진입, 저장 후 복귀. */
export const plantUseSetupPath = (plantId: string): string =>
  `${ROUTES.PLANTS}/${encodeURIComponent(plantId)}/use`;
